import { useEffect, useRef, useState } from "react";

// UI
import { Container, Row, Col } from "react-bootstrap";
import {
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import SaveIcon from "@mui/icons-material/Save";

const RECORDING_STATUSES = ["idle", "recording", "paused", "stopped", "error"];

export default function VoiceRecorder({
  onSave,
  audioSaving,
  format,
  iconSize = 50,
}) {
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const [status, setStatus] = useState(RECORDING_STATUSES[0]);

  const [time, setTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);

  // timer
  useEffect(() => {
    if (status !== RECORDING_STATUSES[1]) return;
    const interval = setInterval(() => {
      setTime((t) => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [status]);

  const requestPermission = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      return true;
    } catch {
      setError("Microphone permission denied.");
      setStatus(RECORDING_STATUSES[4]);
      return false;
    }
  };

  const startRecording = async () => {
    setError(null);
    setAudioUrl(null);
    setTime(0);
    chunksRef.current = [];

    const ok = await requestPermission();
    if (!ok) return;

    const recorder = new MediaRecorder(streamRef.current);
    recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      setAudioUrl(URL.createObjectURL(blob));
      setStatus(RECORDING_STATUSES[3]);
    };

    mediaRecorderRef.current = recorder;
    recorder.start();
    setStatus(RECORDING_STATUSES[1]);
  };

  const pauseRecording = () => {
    mediaRecorderRef.current?.pause();
    setStatus(RECORDING_STATUSES[2]);
  };

  const resumeRecording = () => {
    mediaRecorderRef.current?.resume();
    setStatus(RECORDING_STATUSES[1]);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
  };

  const discardAndRecordAgain = () => {
    setAudioUrl(null);
    setTime(0);
    startRecording();
  };

  const saveRecording = async () => {
    const blob = new Blob(chunksRef.current, {
      type: format ? format : "audio/webm",
    });
    onSave?.(blob);
  };

  const formatTime = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(
      2,
      "0"
    )}`;

  return (
    <Container className="voice-recorder mt-3 mb-3">
      {error && (
        <Row>
          <Col className="text-center mb-3">
            <Typography owner="admin" weight="medium" color="#ec0518ff">
              {error}
            </Typography>
          </Col>
        </Row>
      )}

      <Row>
        {(status === "idle" || status === "error") && (
          <Col>
            <Row className="justify-content-center mb-5">
              <Col xs={7} className="audio-hint">
                <Typography variant="body2" owner="admin" lh="base">
                  Press the microphone button to being recording. During the
                  recording, you can pause / resume using the pause button. To
                  finish, click the stop button.
                </Typography>
              </Col>
            </Row>

            <Row className="text-center mb-3">
              <Col>
                <span className="timer">{formatTime(time)}</span>
              </Col>
            </Row>

            <Row className="text-center flex-column">
              <Col>
                <IconButton
                  onClick={startRecording}
                  sx={{
                    width: iconSize,
                    height: iconSize,
                    lineHeight: `${iconSize}px`,
                    minWidth: 0,
                    borderRadius: "50%",
                    backgroundColor: "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  }}
                >
                  <MicIcon color="white" />
                </IconButton>
              </Col>
              <Col>
                <Button
                  sx={{ marginRight: "0 !important" }}
                  onClick={startRecording}
                >
                  Record
                </Button>
              </Col>
            </Row>
          </Col>
        )}

        {(status === RECORDING_STATUSES[1] ||
          status === RECORDING_STATUSES[2]) && (
          <Col>
            <Row className="justify-content-center mb-5">
              <Col xs={7} className="audio-hint">
                <Typography variant="body2" owner="admin" lh="base">
                  Pause / resume the recording using the pause button. To
                  finish, click the stop button.
                </Typography>
              </Col>
            </Row>

            <Row className="text-center mb-3">
              <Col>
                <span className="timer">{formatTime(time)}</span>
              </Col>
            </Row>

            <Row className="text-center justify-content-center">
              <Col xs={2}>
                <Row className="text-center flex-column">
                  <Col>
                    <IconButton
                      onClick={() => {
                        status === RECORDING_STATUSES[1]
                          ? pauseRecording()
                          : resumeRecording();
                      }}
                      sx={{
                        width: iconSize,
                        height: iconSize,
                        lineHeight: `${iconSize}px`,
                        minWidth: 0,
                        borderRadius: "50%",
                        backgroundColor: "#f9690e",
                        "&:hover": {
                          backgroundColor: "#f9690e",
                        },
                      }}
                    >
                      {status === RECORDING_STATUSES[1] ? (
                        <PauseIcon color="white" />
                      ) : (
                        <PlayArrowIcon color="white" />
                      )}
                    </IconButton>
                  </Col>
                  <Col>
                    <Button
                      sx={{ marginRight: "0 !important" }}
                      onClick={startRecording}
                    >
                      {status === RECORDING_STATUSES[1] ? "Pause" : "Resume"}
                    </Button>
                  </Col>
                </Row>
              </Col>
              <Col xs={2}>
                <Row className="text-center flex-column">
                  <Col>
                    <IconButton
                      onClick={stopRecording}
                      sx={{
                        width: iconSize,
                        height: iconSize,
                        lineHeight: `${iconSize}px`,
                        minWidth: 0,
                        borderRadius: "50%",
                        backgroundColor: "#bf1e2c",
                        "&:hover": {
                          backgroundColor: "#bf1e2c",
                        },
                      }}
                    >
                      <StopIcon color="white" />
                    </IconButton>
                  </Col>
                  <Col>
                    <Button
                      sx={{ marginRight: "0 !important" }}
                      onClick={stopRecording}
                    >
                      Stop
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        )}

        {status === RECORDING_STATUSES[3] && audioUrl && (
          <Col>
            <Row className="justify-content-center mb-5">
              <Col xs={7} className="audio-hint">
                <Typography variant="body2" owner="admin" lh="base">
                  Preview your recording. If ok, click the save button and the
                  audio will be attached to the slide and saved in the Asset
                  Library. If not ok, click the record button and try again.
                </Typography>
              </Col>
            </Row>

            <Row className="text-center mb-3">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <audio controls src={audioUrl} />
                <Row className="text-center flex-column">
                  <Col>
                    <IconButton
                      onClick={saveRecording}
                      sx={{
                        width: iconSize,
                        height: iconSize,
                        lineHeight: `${iconSize}px`,
                        minWidth: 0,
                        borderRadius: "50%",
                        backgroundColor: "#20bf55",
                        "&:hover": {
                          backgroundColor: "#20bf55",
                        },
                      }}
                    >
                      {audioSaving ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : (
                        <SaveIcon color="white" />
                      )}
                    </IconButton>
                  </Col>
                  <Col>
                    <Button
                      sx={{ marginRight: "0 !important" }}
                      onClick={startRecording}
                      disabled={audioSaving}
                    >
                      Save
                    </Button>
                  </Col>
                </Row>
              </Box>
            </Row>

            <Row className="text-center flex-column">
              <Col>
                <IconButton
                  onClick={discardAndRecordAgain}
                  sx={{
                    width: iconSize,
                    height: iconSize,
                    lineHeight: `${iconSize}px`,
                    minWidth: 0,
                    borderRadius: "50%",
                    backgroundColor: "primary.main",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                    },
                  }}
                >
                  <MicIcon color="white" />
                </IconButton>
              </Col>
              <Col>
                <Button
                  sx={{ marginRight: "0 !important" }}
                  onClick={discardAndRecordAgain}
                >
                  Record
                </Button>
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </Container>
  );
}
